"use client";

/**
 * DashboardSidebar — Collapsible left nav (Vercel/Linear style).
 *
 * - Expanded: 240px with label + icon
 * - Collapsed: 64px with icon only and hover tooltips
 * - State persisted in localStorage under "velyq-sidebar-collapsed"
 * - Mobile: hidden; parent should render the existing Sheet fallback
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Logo, LogoIcon } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, FileText, Home, Kanban, Linkedin, Mail, MessageSquare, Mic, PanelLeft, Settings, Target, type LucideIcon } from "lucide-react";
const STORAGE_KEY = "velyq-sidebar-collapsed";
export interface DashboardNavItem {
  href: string;
  icon: LucideIcon;
  labelFr: string;
  labelEn: string;
  activePrefix?: string; // for nested routes, e.g. /resumes/123
  soon?: boolean;
  tourId?: string; // anchor for the onboarding spotlight tour
}
type NavItem = DashboardNavItem;
export const DASHBOARD_NAV_ITEMS: DashboardNavItem[] = [{
  href: "/dashboard",
  icon: Home,
  labelFr: "Dashboard",
  labelEn: "Dashboard",
  tourId: "nav-dashboard"
}, {
  href: "/resumes",
  icon: FileText,
  labelFr: "Mes CVs",
  labelEn: "My CVs",
  activePrefix: "/resumes",
  tourId: "nav-resumes"
}, {
  href: "/ats",
  icon: Target,
  labelFr: "Analyse ATS",
  labelEn: "ATS Analysis",
  activePrefix: "/ats",
  tourId: "nav-ats"
}, {
  href: "/cover-letters",
  icon: Mail,
  labelFr: "Lettres",
  labelEn: "Cover Letters",
  activePrefix: "/cover-letters",
  tourId: "nav-cover-letters"
}, {
  href: "/applications",
  icon: Kanban,
  labelFr: "Suivi des candidatures",
  labelEn: "Application tracker",
  activePrefix: "/applications",
  tourId: "nav-applications"
}, {
  href: "/linkedin",
  icon: Linkedin,
  labelFr: "LinkedIn",
  labelEn: "LinkedIn",
  activePrefix: "/linkedin",
  tourId: "nav-linkedin"
}, {
  href: "/interviews",
  icon: MessageSquare,
  labelFr: "Entretiens",
  labelEn: "Interviews",
  activePrefix: "/interviews",
  tourId: "nav-interviews"
}, {
  href: "/training",
  icon: Mic,
  labelFr: "Entraînement",
  labelEn: "Training",
  activePrefix: "/training",
  tourId: "nav-training"
}];
const SOON_ITEMS: NavItem[] = [];
interface DashboardSidebarProps {
  locale: string;
}
export function DashboardSidebar(t0) {
  const $ = _c(54);
  const {
    locale
  } = t0;
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
      if (typeof window === "undefined") {
        return;
      }
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "1") {
        setCollapsed(true);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== collapsed) {
    t3 = () => {
      const next = !collapsed;
      setCollapsed(next);
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      }
    };
    $[2] = collapsed;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const toggle = t3;
  let t4;
  if ($[4] !== pathname) {
    t4 = item => {
      if (!pathname) {
        return false;
      }
      const p = pathname.replace(/^\/(fr|en)(?=\/|$)/, "");
      if (item.activePrefix) {
        return p.startsWith(item.activePrefix);
      }
      return p === item.href;
    };
    $[4] = pathname;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const isActive = t4;
  const width = collapsed ? "w-[64px]" : "w-60";
  const t5 = `sticky top-0 hidden h-screen shrink-0 self-start border-r bg-muted/20 backdrop-blur-sm transition-[width] duration-200 ease-out lg:flex lg:flex-col ${mounted ? width : "w-60"}`;
  let t6;
  if ($[6] !== collapsed) {
    t6 = <div className="flex h-16 shrink-0 items-center border-b px-4">{collapsed ? <div className="mx-auto"><LogoIcon size={28} /></div> : <Logo size="sm" showBeta={true} />}</div>;
    $[6] = collapsed;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== collapsed || $[9] !== isActive || $[10] !== locale) {
    t7 = DASHBOARD_NAV_ITEMS.map(item_0 => {
      const active = isActive(item_0);
      const label = locale === "fr" ? item_0.labelFr : item_0.labelEn;
      const linkClasses = `group flex items-center rounded-lg text-sm font-medium transition-colors ${active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"} ${collapsed ? "h-10 w-10 justify-center" : "h-9 gap-3 px-3"}`;
      const link = <Link href={item_0.href} className={linkClasses} data-tour={item_0.tourId}><item_0.icon className="h-4.5 w-4.5 shrink-0" />{!collapsed && <span className="truncate">{label}</span>}</Link>;
      return <li key={item_0.href}>{collapsed ? <Tooltip><TooltipTrigger asChild={true}>{link}</TooltipTrigger><TooltipContent side="right" sideOffset={8}>{label}</TooltipContent></Tooltip> : link}</li>;
    });
    $[8] = collapsed;
    $[9] = isActive;
    $[10] = locale;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <ul className="space-y-1">{t7}</ul>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== collapsed || $[15] !== locale) {
    t9 = SOON_ITEMS.length > 0 && <div className={`mt-6 ${collapsed ? "" : "px-3"}`}>{!collapsed && <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">{locale === "fr" ? "Bient\xF4t" : "Coming soon"}</p>}<ul className="space-y-1">{SOON_ITEMS.map(item_1 => {
          const label_0 = locale === "fr" ? item_1.labelFr : item_1.labelEn;
          const linkClasses_0 = `group flex items-center rounded-lg text-sm text-muted-foreground/60 transition-colors hover:text-muted-foreground ${collapsed ? "h-10 w-10 justify-center" : "h-9 gap-3 px-3"}`;
          const trigger = <Link href={item_1.href} className={linkClasses_0}><item_1.icon className="h-4.5 w-4.5 shrink-0 opacity-70" />{!collapsed && <><span className="flex-1 truncate">{label_0}</span><span className="rounded-full border border-primary/25 bg-primary/5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-primary">Soon</span></>}</Link>;
          return <li key={item_1.href}>{collapsed ? <Tooltip><TooltipTrigger asChild={true}>{trigger}</TooltipTrigger><TooltipContent side="right" sideOffset={8}>{label_0} · {locale === "fr" ? "bient\xF4t" : "soon"}</TooltipContent></Tooltip> : trigger}</li>;
        })}</ul></div>;
    $[14] = collapsed;
    $[15] = locale;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <nav className="flex-1 overflow-y-auto px-2 py-4">{t8}{t9}</nav>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== collapsed) {
    t11 = !collapsed && <div className="flex justify-center pb-2"><LanguageSwitcher /></div>;
    $[20] = collapsed;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const label_1 = locale === "fr" ? "Param\xE8tres" : "Settings";
  const linkClasses_1 = `group flex items-center rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted/60 hover:text-foreground ${collapsed ? "h-10 w-10 justify-center" : "h-9 gap-3 px-3"}`;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Settings className="h-4.5 w-4.5 shrink-0" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== collapsed || $[24] !== label_1) {
    t13 = !collapsed && <span className="truncate">{label_1}</span>;
    $[23] = collapsed;
    $[24] = label_1;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== linkClasses_1 || $[27] !== t13) {
    t14 = <Link href="/settings" className={linkClasses_1} data-tour="nav-settings">{t12}{t13}</Link>;
    $[26] = linkClasses_1;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const link_0 = t14;
  let t15;
  if ($[29] !== collapsed || $[30] !== label_1 || $[31] !== link_0) {
    t15 = collapsed ? <Tooltip><TooltipTrigger asChild={true}>{link_0}</TooltipTrigger><TooltipContent side="right" sideOffset={8}>{label_1}</TooltipContent></Tooltip> : link_0;
    $[29] = collapsed;
    $[30] = label_1;
    $[31] = link_0;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const t16 = t15;
  const label_2 = collapsed ? locale === "fr" ? "D\xE9plier le menu" : "Expand menu" : locale === "fr" ? "R\xE9duire le menu" : "Collapse menu";
  const t17 = `group flex items-center rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted/60 hover:text-foreground ${collapsed ? "h-10 w-10 justify-center" : "h-9 w-full gap-3 px-3"}`;
  let t18;
  if ($[33] !== collapsed || $[34] !== label_2) {
    t18 = collapsed ? <PanelLeft className="h-4.5 w-4.5" /> : <><ChevronLeft className="h-4.5 w-4.5 shrink-0" /><span className="truncate">{label_2}</span></>;
    $[33] = collapsed;
    $[34] = label_2;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== label_2 || $[37] !== t17 || $[38] !== t18 || $[39] !== toggle) {
    t19 = <button type="button" onClick={toggle} className={t17} aria-label={label_2}>{t18}</button>;
    $[36] = label_2;
    $[37] = t17;
    $[38] = t18;
    $[39] = toggle;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const btn = t19;
  let t20;
  if ($[41] !== btn || $[42] !== collapsed || $[43] !== label_2) {
    t20 = collapsed ? <Tooltip><TooltipTrigger asChild={true}>{btn}</TooltipTrigger><TooltipContent side="right" sideOffset={8}>{label_2}</TooltipContent></Tooltip> : btn;
    $[41] = btn;
    $[42] = collapsed;
    $[43] = label_2;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = t20;
  let t22;
  if ($[45] !== t11 || $[46] !== t16 || $[47] !== t21) {
    t22 = <div className="shrink-0 border-t px-2 py-3 space-y-1">{t11}{t16}{t21}</div>;
    $[45] = t11;
    $[46] = t16;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t10 || $[50] !== t22 || $[51] !== t5 || $[52] !== t6) {
    t23 = <TooltipProvider delayDuration={100}><aside className={t5}>{t6}{t10}{t22}</aside></TooltipProvider>;
    $[49] = t10;
    $[50] = t22;
    $[51] = t5;
    $[52] = t6;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
}